/**
 * Regras da tela de Campo na Visão Geral (#6505): o motoboy, no celular.
 *
 * Pergunta da tela: qual é a minha rota de hoje?
 *   - rota de hoje: a rota com a data de hoje (Rotas comerciais), as paradas feitas e a
 *     próxima; sem rota hoje, a próxima marcada;
 *   - empresas sem visita: as que entraram em rota do motoboy, em dia que já passou, e nunca
 *     foram visitadas (em nenhuma rota). É a aba "Já agendadas" da lista da #6307; a lista
 *     nova (3.052 empresas em blocos) está em planilha e entra no app quando virar rota.
 *
 *   node --experimental-strip-types --test src/lib/visaoGeral/__tests__/campo.test.ts
 */

export interface NegociacaoDaParada {
  id: number;
  titulo: string | null;
  cidade: string | null;
  bairro: string | null;
  latitude: number | null;
  longitude: number | null;
  geocode_status?: string | null;
  consumo_medio_kwh?: number | string | null;
  /** Situação de hoje da negociação (resultado da semana, #6507). */
  status?: string | null;
  stage?: string | null;
}

export interface ParadaDeRota {
  id: string;
  posicao: number;
  status: string;
  negociacao_id: number | null;
  visitado_em?: string | null;
  numero_medidor?: string | null;
  negociacoes: NegociacaoDaParada | null;
}

export interface RotaDoMotoboy {
  id: string;
  nome: string | null;
  data: string;
  status: string;
  rota_paradas: ParadaDeRota[];
}

const ABERTA = new Set(['planejada', 'agendada', 'reagendada', 'em_execucao']);

export interface RotaDeHoje {
  rota: RotaDoMotoboy | null;
  paradas: number;
  feitas: number;
  /** A próxima parada ainda não visitada, na ordem da rota. */
  proxima: ParadaDeRota | null;
  pendentes: ParadaDeRota[];
  /** Sem rota hoje: a próxima rota marcada. */
  proximaRota: RotaDoMotoboy | null;
  /** Rotas de dias que já passaram que ficaram abertas (planejadas ou em execução). */
  abertasAntigas: RotaDoMotoboy[];
}

export function rotaDeHoje(rotas: readonly RotaDoMotoboy[], hojeIso: string): RotaDeHoje {
  const validas = rotas.filter(r => r.status !== 'cancelada');
  const deHoje = validas.filter(r => r.data === hojeIso)
    .sort((a, b) => Number(b.status === 'em_execucao') - Number(a.status === 'em_execucao'))[0] ?? null;
  const paradas = deHoje ? [...deHoje.rota_paradas].sort((a, b) => a.posicao - b.posicao) : [];
  const pendentes = paradas.filter(p => p.status !== 'visitado');
  return {
    rota: deHoje,
    paradas: paradas.length,
    feitas: paradas.length - pendentes.length,
    proxima: pendentes[0] ?? null,
    pendentes,
    proximaRota: deHoje ? null : validas.filter(r => r.data > hojeIso && ABERTA.has(r.status)).sort((a, b) => a.data.localeCompare(b.data))[0] ?? null,
    abertasAntigas: validas.filter(r => r.data < hojeIso && ABERTA.has(r.status)).sort((a, b) => a.data.localeCompare(b.data)),
  };
}

export interface EmpresaSemVisita {
  negociacao: NegociacaoDaParada;
  /** Data da última rota em que ela entrou. */
  ultimaRota: string;
  vezes: number;
}

/**
 * Empresas que entraram em rota do motoboy em dia que já passou e nunca foram visitadas.
 * Visita em qualquer rota (inclusive cancelada) tira a empresa da lista; parada de rota
 * cancelada não conta como pendente (foi tirada de propósito).
 */
export function empresasSemVisita(rotas: readonly RotaDoMotoboy[], hojeIso: string): EmpresaSemVisita[] {
  const visitadas = new Set<number>();
  for (const r of rotas) for (const p of r.rota_paradas) if (p.status === 'visitado' && p.negociacao_id != null) visitadas.add(p.negociacao_id);
  const porNegociacao = new Map<number, EmpresaSemVisita>();
  for (const r of rotas) {
    if (r.status === 'cancelada' || r.data >= hojeIso) continue;
    for (const p of r.rota_paradas) {
      if (p.negociacao_id == null || !p.negociacoes || visitadas.has(p.negociacao_id) || p.status === 'visitado') continue;
      const atual = porNegociacao.get(p.negociacao_id);
      if (!atual) porNegociacao.set(p.negociacao_id, { negociacao: p.negociacoes, ultimaRota: r.data, vezes: 1 });
      else { atual.vezes++; if (r.data > atual.ultimaRota) atual.ultimaRota = r.data; }
    }
  }
  return [...porNegociacao.values()].sort((a, b) =>
    (Number(b.negociacao.consumo_medio_kwh) || 0) - (Number(a.negociacao.consumo_medio_kwh) || 0) || b.ultimaRota.localeCompare(a.ultimaRota));
}

/** Distância em km entre dois pontos (fórmula de haversine). */
export function distanciaKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const r = 6371;
  const rad = (g: number) => (g * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

/** A lista na ordem da mais perto de `de`; empresa sem ponto vai para o fim. */
export function maisPertoPrimeiro(lista: readonly EmpresaSemVisita[], de: { lat: number; lng: number }): (EmpresaSemVisita & { km: number | null })[] {
  return lista
    .map(e => {
      const { latitude: lat, longitude: lng } = e.negociacao;
      return { ...e, km: lat != null && lng != null ? distanciaKm(de, { lat: Number(lat), lng: Number(lng) }) : null };
    })
    .sort((a, b) => (a.km ?? Infinity) - (b.km ?? Infinity));
}
